import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../../lib/supabase';
import usageTracker from '../../services/usageTracker';

// Create context
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userMetadata, setUserMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);

      // Check for existing session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        const { user: currentUser } = session;
        setUser(currentUser);
        
        // Fetch user metadata from our custom table
        await fetchUserMetadata(currentUser.id);
      } else {
        setUser(null);
        setUserMetadata(null);
        
        // Check for anonymous user data in localStorage
        initializeAnonUserData();
      }

      // Set up auth state listener
      const { data: { subscription } } = await supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (session && session.user) {
            setUser(session.user);
            await fetchUserMetadata(session.user.id);
            
            // If the event is a sign-in, transfer any anonymous usage data
            if (event === 'SIGNED_IN') {
              await transferAnonDataToUser(session.user.id);
            }
          } else {
            setUser(null);
            setUserMetadata(null);
            
            // Re-initialize anonymous user data
            initializeAnonUserData();
          }
        }
      );

      setLoading(false);

      // Cleanup function
      return () => {
        subscription?.unsubscribe();
      };
    };

    initAuth();
  }, []);

  // Initialize anonymous user data in localStorage if not exists
  const initializeAnonUserData = () => {
    const anonData = localStorage.getItem('anon_user_data');
    if (!anonData) {
      const newAnonData = {
        free_trials_used: 0,
        created_at: new Date().toISOString()
      };
      localStorage.setItem('anon_user_data', JSON.stringify(newAnonData));
    }
  };

  // Transfer anonymous user data to registered user
  const transferAnonDataToUser = async (userId) => {
    const anonData = localStorage.getItem('anon_user_data');
    if (!anonData) return;
    
    try {
      const parsedData = JSON.parse(anonData);
      
      // Get current user metadata
      const { data: metadata } = await supabase
        .from('users_metadata')
        .select('*')
        .eq('id', userId)
        .single();
        
      if (metadata) {
        // Only transfer free trials if the user doesn't already have a value
        const updatedTrials = metadata.free_trials_used || parsedData.free_trials_used || 0;
        
        await supabase
          .from('users_metadata')
          .update({ free_trials_used: updatedTrials })
          .eq('id', userId);
      }
      
      // Clear anonymous data to prevent reuse
      localStorage.removeItem('anon_user_data');
      
      // Also update the usage tracker with the browser fingerprint
      await usageTracker.linkUserToFingerprint(userId);
    } catch (error) {
      console.error('Error transferring anonymous data:', error);
    }
  };

  // Fetch user metadata from our custom table
  const fetchUserMetadata = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('users_metadata')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user metadata:', error);
        return;
      }

      setUserMetadata(data);
    } catch (error) {
      console.error('Error in fetchUserMetadata:', error);
    }
  };

  // Sign up function
  const signUp = async (email, password, displayName) => {
    try {
      setAuthError(null);
      setLoading(true);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName
          }
        }
      });

      if (error) {
        setAuthError(error.message);
        return { error };
      }

      // The user metadata will be created by the database trigger
      // But we can update the display name here if needed
      if (data?.user) {
        await supabase
          .from('users_metadata')
          .update({ display_name: displayName })
          .eq('id', data.user.id);
          
        // Link this user to the browser fingerprint for persistent tracking
        await usageTracker.linkUserToFingerprint(data.user.id);
      }

      return { data };
    } catch (error) {
      setAuthError(error.message);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  // Sign in function
  const signIn = async (email, password) => {
    try {
      setAuthError(null);
      setLoading(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        setAuthError(error.message);
        return { error };
      }

      // Close login modal if it was open
      setShowLoginModal(false);

      // Link this user to the browser fingerprint for persistent tracking
      if (data?.user) {
        await usageTracker.linkUserToFingerprint(data.user.id);
      }

      // Check if user needs to pay after successful login
      setTimeout(() => {
        if (needsPayment()) {
          setShowPaymentModal(true);
        }
      }, 500);

      return { data };
    } catch (error) {
      setAuthError(error.message);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      setSigningOut(true);
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }
      
      // Clear any stored fingerprint-user association
      await usageTracker.unlinkUserFromFingerprint();
      
      setUser(null);
      setUserMetadata(null);
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setSigningOut(false);
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      setAuthError(null);
      setLoading(true);

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/#/reset-password`,
      });

      if (error) {
        setAuthError(error.message);
        return { error };
      }

      return { success: true };
    } catch (error) {
      setAuthError(error.message);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    try {
      setAuthError(null);
      setLoading(true);

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        setAuthError(error.message);
        return { error };
      }

      return { success: true };
    } catch (error) {
      setAuthError(error.message);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  // Update user metadata
  const updateUserMetadata = async (updates) => {
    if (!user) return { error: 'No user is logged in' };

    try {
      const { data, error } = await supabase
        .from('users_metadata')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating user metadata:', error);
        return { error };
      }

      setUserMetadata(data);
      return { data };
    } catch (error) {
      console.error('Error in updateUserMetadata:', error);
      return { error };
    }
  };

  // Record a free trial use
  const recordFreeTrialUse = async () => {
    if (user) {
      // Logged-in user: update database
      try {
        const newTrialCount = (userMetadata?.free_trials_used || 0) + 1;
        
        const { data, error } = await supabase
          .from('users_metadata')
          .update({
            free_trials_used: newTrialCount,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id)
          .select()
          .single();

        if (error) {
          console.error('Error recording free trial use:', error);
          return { error };
        }

        setUserMetadata(data);
        
        // Also update the usage tracker for consistent analytics
        await usageTracker.recordSuccessfulCalculation(true, user.id);
        
        // Check if user has used all free trials and needs to pay
        if (newTrialCount >= 3 && !isPremium) {
          setShowPaymentModal(true);
        }
        
        return { data };
      } catch (error) {
        console.error('Error in recordFreeTrialUse:', error);
        return { error };
      }
    } else {
      // Anonymous user: update localStorage and tracking service
      try {
        const anonData = localStorage.getItem('anon_user_data');
        if (anonData) {
          const data = JSON.parse(anonData);
          data.free_trials_used = (data.free_trials_used || 0) + 1;
          data.last_use = new Date().toISOString();
          localStorage.setItem('anon_user_data', JSON.stringify(data));
          
          // Also update the usage tracker for consistent analytics
          await usageTracker.recordSuccessfulCalculation(false);
          
          // Check if anonymous user has used all free trials and should sign up
          if (data.free_trials_used >= 3) {
            setShowLoginModal(true);
          }
          
          return { data };
        }
        return { error: 'No anonymous user data found' };
      } catch (error) {
        console.error('Error updating anonymous user data:', error);
        return { error };
      }
    }
  };

  // Update premium status
  const updatePremiumStatus = async (isPremium, paymentId = null) => {
    if (!user) return { error: 'No user is logged in' };

    try {
      const updates = {
        premium_status: isPremium,
        subscription_active: isPremium,
        updated_at: new Date().toISOString()
      };

      if (isPremium) {
        updates.premium_since = new Date().toISOString();
        if (paymentId) updates.payment_id = paymentId;
      }

      const { data, error } = await supabase
        .from('users_metadata')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating premium status:', error);
        return { error };
      }

      // Also update the usage tracker for consistent analytics
      await usageTracker.recordPayment(paymentId, user.id);

      setUserMetadata(data);
      return { data };
    } catch (error) {
      console.error('Error in updatePremiumStatus:', error);
      return { error };
    }
  };

  // Check remaining free trials
  const getRemainingFreeTrials = () => {
    const maxFreeTrials = 3;
    
    if (user) {
      // Logged-in user: check from userMetadata
      if (!userMetadata) return maxFreeTrials;
      
      if (userMetadata.premium_status && userMetadata.subscription_active) {
        return Infinity; // Premium users have unlimited trials
      }
      
      return Math.max(0, maxFreeTrials - (userMetadata.free_trials_used || 0));
    } else {
      // Anonymous user: check from localStorage
      try {
        const anonData = localStorage.getItem('anon_user_data');
        if (anonData) {
          const data = JSON.parse(anonData);
          return Math.max(0, maxFreeTrials - (data.free_trials_used || 0));
        }
        return maxFreeTrials;
      } catch (error) {
        console.error('Error reading anonymous user data:', error);
        return maxFreeTrials;
      }
    }
  };

  // Check if user needs to pay
  const needsPayment = () => {
    const maxFreeTrials = 3;
    
    if (user && userMetadata) {
      // Logged-in user
      if (userMetadata.premium_status && userMetadata.subscription_active) {
        return false; // Premium users don't need to pay
      }
      
      return (userMetadata.free_trials_used || 0) >= maxFreeTrials;
    } else {
      // Anonymous user
      try {
        const anonData = localStorage.getItem('anon_user_data');
        if (anonData) {
          const data = JSON.parse(anonData);
          return (data.free_trials_used || 0) >= maxFreeTrials;
        }
        return false;
      } catch (error) {
        console.error('Error reading anonymous user data:', error);
        return false;
      }
    }
  };
  
  // Toggle subscription status
  const toggleSubscriptionStatus = async () => {
    if (!user || !userMetadata) return { error: 'No user is logged in' };
    
    try {
      const newStatus = !userMetadata.subscription_active;
      
      const { data, error } = await supabase
        .from('users_metadata')
        .update({
          subscription_active: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error toggling subscription status:', error);
        return { error };
      }

      setUserMetadata(data);
      
      // Show payment modal if user reactivates subscription
      if (newStatus && !userMetadata.premium_status) {
        setShowPaymentModal(true);
      }
      
      return { data };
    } catch (error) {
      console.error('Error in toggleSubscriptionStatus:', error);
      return { error };
    }
  };
  
  // Get display name for the user
  const getDisplayName = () => {
    if (user) {
      return userMetadata?.display_name || user.email?.split('@')[0] || 'User';
    }
    return null;
  };
  
  // Check if user is premium
  const isPremium = userMetadata?.premium_status && userMetadata?.subscription_active || false;

  const contextValue = {
    user,
    userMetadata,
    loading,
    authError,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    updateUserMetadata,
    recordFreeTrialUse,
    updatePremiumStatus,
    getRemainingFreeTrials,
    needsPayment,
    toggleSubscriptionStatus,
    isPremium,
    displayName: getDisplayName(),
    showLoginModal,
    setShowLoginModal,
    showPaymentModal,
    setShowPaymentModal,
    signingOut
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;